import React from 'react';
import PropTypes from 'prop-types';
import { withStyles, MenuItem, Grid } from '@material-ui/core';
import { TextLink, Input as FormInput, Button } from '@ds/react-components';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { connect } from 'react-redux';
import { ajaxActions } from 'common/ajax/store.js';
import ToastMessage from 'common/alert/ToastMessage.js';
import { actionCreators } from './store.js';

const styles = (theme) => ({
  root: {},
  paper: {
    maxWidth: '800px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    lineHeight: 1.33,
  },
  content: {
    textAlign: 'left',
  },
  indicator: {
    fontSize: '14px',
    lineHeight: 1.33,
    color: '#666',
    paddingTop: '5px',
    [theme.breakpoints.down('xs')]: {
      textAlign: 'left',
    },
    [theme.breakpoints.up('sm')]: {
      textAlign: 'right',
    },
  },
  edit: {
    position: 'relative',
    display: 'flex',
    float: 'right',
    top: '30px',
    right: '30px',
    color: '#004A8C !important',
    fontWeight: 'bold',
    fontStyle: 'normal',
    '&:hover': {
      color: '#002A64 !important',
    },
  },
  split: {
    border: `1px solid ${theme.palette.common.inputBorder}`,
    padding: '24px 32px',
  },
  updateButton: {
    backgroundColor: '#6AAC40 !important',
    borderColor: '#6AAC40',
    color: '#fff',
    padding: '10px 16px !important',
    '&:hover': {
      backgroundColor: '#387C20 !important',
      borderColor: '#387C20',
    },
  },
  cancelButton: {
    color: '#9A9EA0',
    borderColor: '#9A9EA0',
    padding: '10px 16px !important',
    marginLeft: '16px',
    '&:hover': {
      color: '#626668',
      borderColor: '#626668',
      backgroundColor: '#fff !important',
    },
  },
  inputBox: {
    background: '#FFFFFF',
    borderRadius: '6px',
    borderColor: '#C7D0D9',
    marginTop: 0,
    fontSize: '14px',
    '&:disabled': {
      backgroundColor: '#F1F4F7',
    },
  },
  hideSvg: {
    marginTop: '4px !important',
    '& svg': {
      display: 'none !important',
    },
  },
  selectRoot: {
    marginTop: '4px !important',
    '& svg:last-child': {
      display: 'none !important',
    },
  },
  btnAlignLeft: {
    justifyContent: 'flex-start',
    padding: '24px 0px 24px 32px',
    border: `1px solid #c7d0d9`,
  },
  dialogContent: {
    overflow: 'hidden',
    padding: '24px 32px',
  },
  gridContent: {
    paddingBottom: '4px',
  },
  grid: {
    paddingTop: '12px !important',
    paddingBottom: '12px !important',
  },
  toast: {
    margin: '12px 0',
  },
  labelTitle: {
    color: '#666 !important',
    '& span': {
      color: '#FF0000',
    },
  },
});

class EditAddress extends React.PureComponent {
  constructor(props) {
    super(props);
    const { loadRegion, currentAddress, profileDetails } = this.props;
    const { regions } = profileDetails;
    this.state = {
      openDialog: false,
      addressId: {
        name: 'AddressId',
        value: currentAddress.addressId,
        error: '',
      },
      address1: {
        name: 'Address line 1',
        value: currentAddress.streetNumber,
        error: '',
      },
      address2: {
        name: 'Address line 2',
        value: currentAddress.street,
        error: '',
      },
      suburb: {
        name: 'Suburb',
        value: currentAddress.suburb,
        error: '',
      },
      city: {
        name: 'Town / City',
        value: currentAddress.city,
        error: '',
      },
      state: {
        name: 'State / Territory / Region',
        value: currentAddress.state,
        error: '',
      },
      postCode: {
        name: 'Postal code',
        value: currentAddress.postcode,
        error: '',
      },
    };

    // only reload region if null in state
    if (regions === null) {
      loadRegion(currentAddress.countryCode);
    }
  }

  componentDidUpdate = (prevProps) => {
    const { currentAddress } = this.props;
    const { openDialog } = this.state;

    if (
      JSON.stringify(currentAddress) !==
      JSON.stringify(prevProps.currentAddress)
    ) {
      this.setState({
        openDialog: !openDialog,
      });
      this.props.updateAjaxStatus();
    }
  };

  componentWillUnmount = () => {
    this.props.resetAddressRegions();
  };

  handleOpenClose = () => {
    const { currentAddress, profileDetails } = this.props;
    const { regions } = profileDetails;
    const { openDialog } = this.state;
    this.setState({
      openDialog: !openDialog,
    });

    // reset validation message when open promts
    if (!openDialog === true) {
      this.setState((prevState) => ({
        ...prevState.openDialog,
        addressId: { ...prevState.addressId, error: '' },
        address1: { ...prevState.address1, error: '' },
        address2: { ...prevState.address2, error: '' },
        suburb: { ...prevState.suburb, error: '' },
        city: {
          ...prevState.city,
          error:
            currentAddress.countryCode.toUpperCase() === 'NZ' &&
            !regions.includes(currentAddress.city)
              ? this.state.city.name + ' is required.'
              : '',
        },
        state: {
          ...prevState.state,
          error:
            currentAddress.countryCode.toUpperCase() === 'AUS' &&
            !regions.includes(currentAddress.state.trim())
              ? this.state.state.name + ' is required.'
              : '',
        },
        postCode: { ...prevState.postCode, error: '' },
      }));
    } else {
      // reset ajax status when closed pop up
      this.props.resetAjaxStatus();
    }

    this.props.setActionStatus(!openDialog);
  };

  handleInputChange = (event) => {
    const { target } = event;
    const { regions } = this.props.profileDetails;

    // no validation if textField not include helperText prop
    if (target.name !== 'city' && target.name !== 'state' && !target.required) {
      this.setState((prevState) => ({
        [target.name]: {
          ...prevState[target.name],
          value: target.value,
          error: '',
        },
      }));
      return;
    }

    // Check if input value is empty
    if (target.value === '') {
      this.setState((prevState) => ({
        [target.name]: {
          ...prevState[target.name],
          error: this.state[target.name].name + ' is required.',
        },
      }));
      // Check Post code field only allow number
    } else if (target.name === 'postCode') {
      const reg = /^[0-9]+$/;
      if (reg.test(target.value)) {
        this.setState((prevState) => ({
          [target.name]: {
            ...prevState[target.name],
            value: target.value,
            error: '',
          },
        }));
      } else {
        this.setState((prevState) => ({
          [target.name]: {
            ...prevState[target.name],
            error: this.state[target.name].name + ' only allow numbers.',
          },
        }));
      }
    } else if (target.name === 'city' || target.name === 'state') {
      if (regions.includes(target.value)) {
        this.setState((prevState) => ({
          [target.name]: {
            ...prevState[target.name],
            value: target.value,
            error: '',
          },
        }));
      } else {
        this.setState((prevState) => ({
          [target.name]: {
            ...prevState[target.name],
            error: this.state[target.name].name + ' is required.',
          },
        }));
      }
    } else {
      this.setState((prevState) => ({
        [target.name]: {
          ...prevState[target.name],
          value: target.value,
          error: '',
        },
      }));
    }
  };

  handleSubmit = (event) => {
    event.preventDefault();
    const { profileDetails, currentAddress, localisation } = this.props;
    const { countryISOCode } = localisation.prompts;

    // get all input field state except openDialog state
    const inputs = Object.keys(this.state).reduce((object, key) => {
      if (key !== 'openDialog') {
        object[key] = this.state[key];
      }
      return object;
    }, {});

    // check no error message
    if (
      Object.keys(inputs).filter((c) => this.state[c].error !== '').length === 0
    ) {
      let model = {
        AddressLine1: this.state.address1.value,
        AddressLine2: this.state.address2.value,
        Suburb: this.state.suburb.value,
        City: currentAddress.countryCode === 'NZ' ? this.state.city.value : '',
        State: this.state.state.value,
        PostCode: this.state.postCode.value,
        CountryISOCode:
          countryISOCode[currentAddress.countryCode.toUpperCase()],
        IsPrimary: true, // if address is not primary, set it to primary
        AddressType: currentAddress.addressTypeFull,
      };

      this.props.updateCurrentAddress(
        profileDetails.adfitNo,
        currentAddress.addressNo,
        JSON.stringify(model)
      );
    }
  };

  render() {
    const {
      classes,
      linkText,
      currentAddress,
      title,
      profileDetails,
      ajax,
    } = this.props;

    const { error } = ajax;
    const { regions } = profileDetails;
    const country = currentAddress.countryCode;
    const content =
      country === 'NZ' || country === 'AUS' ? (
        country === 'NZ' ? (
          <Grid container spacing={4} classes={{ root: classes.gridContent }}>
            <Grid item xs={12} classes={{ root: classes.grid }}>
              <label htmlFor="address1" className={classes.labelTitle}>
                {this.state.address1.name} <span>*</span>
              </label>
              <FormInput
                id="address1"
                name="address1"
                inputProps={{ maxLength: 60 }}
                classes={{ root: classes.hideSvg, input: classes.inputBox }}
                defaultValue={currentAddress.streetNumber}
                onChange={this.handleInputChange}
                error={this.state.address1.error != ''}
                helperText={this.state.address1.error}
                required
              />
            </Grid>
            <Grid item xs={12} classes={{ root: classes.grid }}>
              <FormInput
                id="address2"
                name="address2"
                label={this.state.address2.name}
                inputProps={{ maxLength: 60 }}
                classes={{ root: classes.hideSvg, input: classes.inputBox }}
                defaultValue={currentAddress.street}
                onChange={this.handleInputChange}
                error={this.state.address2.error != ''}
                helperText={this.state.address2.error}
              />
            </Grid>
            <Grid item xs={12} sm={6} classes={{ root: classes.grid }}>
              <FormInput
                id="suburb"
                name="suburb"
                label={this.state.suburb.name}
                inputProps={{ maxLength: 50 }}
                classes={{ root: classes.hideSvg, input: classes.inputBox }}
                defaultValue={
                  currentAddress.suburb
                    ? currentAddress.suburb
                    : currentAddress.city
                }
                onChange={this.handleInputChange}
                error={this.state.suburb.error !== ''}
                helperText={this.state.suburb.error}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              classes={{
                root: classes.grid,
              }}>
              <label htmlFor="city" className={classes.labelTitle}>
                {this.state.city.name} <span>*</span>
              </label>
              <FormInput
                id="city"
                name="city"
                classes={{ root: classes.selectRoot, input: classes.inputBox }}
                defaultValue={currentAddress.city && currentAddress.city.trim()}
                onChange={this.handleInputChange}
                error={this.state.city.error !== ''}
                helperText={this.state.city.error}
                select>
                {regions && !regions.includes(currentAddress.city) && (
                  <MenuItem value={currentAddress.city}>Select</MenuItem>
                )}
                {regions !== null ? (
                  regions.map((code, index) => {
                    return (
                      <MenuItem value={code} key={index}>
                        {code}
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem value={currentAddress.city}>
                    {currentAddress.city}
                  </MenuItem>
                )}
              </FormInput>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              classes={{
                root: classes.grid,
              }}>
              <label htmlFor="postCode" className={classes.labelTitle}>
                {this.state.postCode.name} <span>*</span>
              </label>
              <FormInput
                id="postCode"
                name="postCode"
                inputProps={{ maxLength: 10 }}
                classes={{ root: classes.hideSvg, input: classes.inputBox }}
                defaultValue={currentAddress.postcode}
                onChange={this.handleInputChange}
                error={this.state.postCode.error !== ''}
                helperText={this.state.postCode.error}
                required
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              classes={{
                root: classes.grid,
              }}>
              <FormInput
                id="country"
                name="country"
                label="Country"
                classes={{ root: classes.hideSvg, input: classes.inputBox }}
                defaultValue={currentAddress.country}
                disabled
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={4} classes={{ root: classes.gridContent }}>
            <Grid item xs={12} classes={{ root: classes.grid }}>
              <label htmlFor="address1" className={classes.labelTitle}>
                {this.state.address1.name} <span>*</span>
              </label>
              <FormInput
                id="address1"
                name="address1"
                inputProps={{ maxLength: 60 }}
                classes={{ root: classes.hideSvg, input: classes.inputBox }}
                defaultValue={currentAddress.streetNumber}
                onChange={this.handleInputChange}
                error={this.state.address1.error != ''}
                helperText={this.state.address1.error}
                required
              />
            </Grid>
            <Grid item xs={12} classes={{ root: classes.grid }}>
              <FormInput
                id="address2"
                name="address2"
                label={this.state.address2.name}
                inputProps={{ maxLength: 60 }}
                classes={{ root: classes.hideSvg, input: classes.inputBox }}
                defaultValue={currentAddress.street}
                onChange={this.handleInputChange}
                error={this.state.address2.error != ''}
                helperText={this.state.address2.error}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              classes={{
                root: classes.grid,
              }}>
              <label htmlFor="suburb" className={classes.labelTitle}>
                {this.state.suburb.name} <span>*</span>
              </label>
              <FormInput
                id="suburb"
                name="suburb"
                inputProps={{ maxLength: 50 }}
                classes={{ root: classes.hideSvg, input: classes.inputBox }}
                defaultValue={
                  currentAddress.suburb
                    ? currentAddress.suburb
                    : currentAddress.city
                }
                onChange={this.handleInputChange}
                error={this.state.suburb.error !== ''}
                helperText={this.state.suburb.error}
                required
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              classes={{
                root: classes.grid,
              }}>
              <label htmlFor="state" className={classes.labelTitle}>
                {this.state.state.name} <span>*</span>
              </label>
              <FormInput
                id="state"
                name="state"
                classes={{ root: classes.selectRoot, input: classes.inputBox }}
                defaultValue={
                  currentAddress.state && currentAddress.state.trim()
                }
                onChange={this.handleInputChange}
                error={this.state.state.error !== ''}
                helperText={this.state.state.error}
                select>
                {regions && !regions.includes(currentAddress.state) && (
                  <MenuItem value={currentAddress.state}>Select</MenuItem>
                )}
                {regions !== null ? (
                  regions.map((code, index) => {
                    return (
                      <MenuItem value={code} key={index}>
                        {code}
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem value={currentAddress.state}>
                    {currentAddress.state}
                  </MenuItem>
                )}
              </FormInput>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              classes={{
                root: classes.grid,
              }}>
              <label htmlFor="postCode" className={classes.labelTitle}>
                {this.state.postCode.name} <span>*</span>
              </label>
              <FormInput
                id="postCode"
                name="postCode"
                inputProps={{ maxLength: 10 }}
                classes={{ root: classes.hideSvg, input: classes.inputBox }}
                defaultValue={currentAddress.postcode}
                onChange={this.handleInputChange}
                error={this.state.postCode.error !== ''}
                helperText={this.state.postCode.error}
                required
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              classes={{
                root: classes.grid,
              }}>
              <FormInput
                id="country"
                name="country"
                label="Country"
                classes={{ root: classes.hideSvg, input: classes.inputBox }}
                defaultValue={currentAddress.country}
                disabled
              />
            </Grid>
          </Grid>
        )
      ) : null;

    return (
      <React.Fragment>
        <TextLink
          to={'#'}
          onClick={this.handleOpenClose}
          className={classes.edit}>
          {linkText}
        </TextLink>
        <Dialog
          className={classes.root}
          classes={{ paper: classes.paper }}
          fullWidth
          open={this.state.openDialog}
          onClose={this.handleOpenClose}>
          <DialogTitle className={classes.split}>
            <Grid container spacing={0}>
              <Grid item xs={12} sm={7} classes={{ root: classes.title }}>
                <div>{title}</div>
              </Grid>
              <Grid item xs={12} sm={5} classes={{ root: classes.indicator }}>
                <div>
                  <span style={{ color: '#FF0000', marginRight: '2px' }}>
                    *
                  </span>
                  Indicates required field
                </div>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent
            classes={{
              root: classes.dialogContent,
            }}>
            {error &&
              (error.status === 400
                ? error.validationErrors.map((c, i) => {
                    return (
                      <ToastMessage
                        key={i}
                        type={'error'}
                        message={c.errorMessage}
                        classes={{ root: classes.toast }}
                      />
                    );
                  })
                : error.statusText && (
                    <ToastMessage
                      type={'error'}
                      message={error.statusText}
                      classes={{ root: classes.toast }}
                    />
                  ))}
            {content}
          </DialogContent>
          <DialogActions classes={{ root: classes.btnAlignLeft }}>
            <Button
              variant="outlined"
              onClick={this.handleSubmit}
              classes={{ outlined: classes.updateButton }}
              text="Update"
            />
            <Button
              variant="outlined"
              onClick={this.handleOpenClose}
              classes={{ outlined: classes.cancelButton }}
              text="Cancel"
            />
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  }
}

EditAddress.propTypes = {
  classes: PropTypes.object.isRequired,
  linkText: PropTypes.string,
  localisation: PropTypes.object,
};

const mapStateToProps = (state) => ({
  ajax: state.ajax,
  localisation: state.localisation,
});

const mapDispatchToProps = (dispatch) => {
  return {
    setActionStatus: (isEditAction) => {
      dispatch(ajaxActions.setActionStatus(isEditAction));
    },
    updateAjaxStatus: () => {
      dispatch(ajaxActions.updateAjaxStatus());
    },
    resetAjaxStatus: () => {
      dispatch(ajaxActions.resetAjaxStatus());
    },
    updateCurrentAddress: (adfitNo, addressId, model) => {
      dispatch(actionCreators.updateCurrentAddress(adfitNo, addressId, model));
    },
    resetAddressRegions: () => {
      dispatch(actionCreators.resetAddressRegions());
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(EditAddress));
